import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  getDoc,
  doc
} from 'firebase/firestore'
import firebase from '../../firebase'
import { formatInTimeZone } from 'date-fns-tz'

const getSensorData = () => {
  const fetchTimeData = async (sessionId) => {
    console.log(sessionId)
    try {
      const q = query(
        collection(firebase.db, 'data_by_sensor'),
        where('sessionId', '==', sessionId),
        where('sensorId', '==', 'Time'),
        orderBy('binId')
      )
      const querySnapshot = await getDocs(q)
      const fetchedTimeData = []

      querySnapshot.forEach((doc) => {
        const timeStamps = doc.data().values.map(formatToJsDate)
        fetchedTimeData.push({
          deviceId: doc.data().deviceId,
          values: timeStamps
        })
      })

      // Process and combine the fetchedTimeData
      return processAndCombineTimeData(fetchedTimeData)
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  const processAndCombineTimeData = (fetchedTimeData) => {
    const timeDataMap = new Map()
    const timeData = []

    fetchedTimeData.forEach(({ deviceId, values }) => {
      if (!timeDataMap.has(deviceId)) {
        timeDataMap.set(deviceId, [])
      }
      const combinedValues = timeDataMap.get(deviceId).concat(values)
      timeDataMap.set(deviceId, combinedValues)
    })

    timeDataMap.forEach((values, deviceId) => {
      timeData.push({ deviceId, values })
    })
    return timeData
  }

  const fetchTimeDataOffsets = async (sessionId) => {
    try {
      const q = query(
        collection(firebase.db, 'data_by_sensor'),
        where('sessionId', '==', sessionId),
        where('sensorId', '==', 'Time'),
        orderBy('binId')
      )
      const querySnapshot = await getDocs(q)
      const fetchedTimeData = []

      querySnapshot.forEach((doc) => {
        const timeStamps = doc.data().values
        fetchedTimeData.push({
          deviceId: doc.data().deviceId,
          values: timeStamps
        })
      })

      // Process and combine the fetchedTimeData
      return processAndCombineTimeOffsetData(fetchedTimeData)
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  const processAndCombineTimeOffsetData = (fetchedTimeData) => {
    const timeDataMap = new Map()
    const timeData = []

    fetchedTimeData.forEach(({ deviceId, values }) => {
      if (!timeDataMap.has(deviceId)) {
        timeDataMap.set(deviceId, [])
      }
      // Check if there are values and set the first one as the reference time (zero)
      if (values.length > 0) {
        const referenceTime = values[0].toDate().getTime() // Convert the first date to time in milliseconds
        const timeOffsets = values.map(date => date.toDate().getTime() - referenceTime) // Calculate offsets
        const combinedValues = timeDataMap.get(deviceId).concat(timeOffsets)
        timeDataMap.set(deviceId, combinedValues)
      }
    })

    timeDataMap.forEach((values, deviceId) => {
      timeData.push({ deviceId, values })
    })

    return timeData
  }

  const formatToJsDate = (timestamp) => {
    return formatInTimeZone(timestamp.toDate(), 'America/Los_Angeles', 'yyyy-MM-dd HH:mm:ss.SSS').trim()
  }

  const formatTime = (time) => {
    return (
      (time.seconds - 1642620000 + time.nanoseconds / 100000 / 10000.0) * 100000
    )
  }

  const fetchSensorData = async (sessionId, sensorId) => {
    try {
      const sensorData = []
      const q = query(
        collection(firebase.db, 'data_by_sensor'),
        where('sessionId', '==', sessionId),
        where('sensorId', '==', sensorId),
        orderBy('binId')
      )
      const querySnapshot = await getDocs(q)

      querySnapshot.forEach((doc) => {
        sensorData.push({
          deviceId: doc.data().deviceId,
          values: doc.data().values
        })
      })
      return sensorData
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  const fetchremovedTimePoints = async (sessionId) => {
    try {
      const removedTimePoints = []
      const document = doc(firebase.db, 'sessions', sessionId)
      const querySnapshot = await getDoc(document)

      if (querySnapshot.exists()) {
        const data = querySnapshot.data()
        if (data.removedTimePoints) {
          removedTimePoints.push(...data.removedTimePoints)
        }
      }
      return removedTimePoints
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  const fetchDataForChart = async (sessionId, sensorId, userDevices, timeData) => {
    try {
      let sensorData = await fetchSensorData(sessionId, sensorId)
      // If sensorData is empty and sensorId is 'Angle', refetch using 'AngleX'.
      if (!sensorData.length && sensorId === 'Angle') {
        sensorData = await fetchSensorData(sessionId, 'AngleX')
      }
      const sensorDataByTime = []
      for (let i = 0; i < timeData.length; i++) {
        const times = timeData[i].values
        const deviceId = timeData[i].deviceId
        const matchedSensors = sensorData.filter((data) => data.deviceId === deviceId)

        // Concatenate all sensor arrays into a single array
        let allSensors = []
        for (const sensor of matchedSensors) {
          allSensors = allSensors.concat(sensor.values)
        }

        const timeSensorPairs = times.map((time, index) => [time, allSensors[index]])

        sensorDataByTime.push({
          name: userDevices[deviceId],
          data: timeSensorPairs
        })
      }
      return sensorDataByTime
    } catch (error) {
      console.error('Error fetching data for charts', error)
      return [] // Return an empty array or handle the error appropriately
    }
  }

  const fetchSensorLabels = async (deviceName) => {
    try {
      const labelsRef = doc(firebase.db, 'sensorLabels', deviceName)
      const labelsSnapShot = await getDoc(labelsRef)

      if (labelsSnapShot.exists()) {
        return labelsSnapShot.data()
      }
    } catch (error) {
      console.log('No such document!')
    }
  }

  const fetchCompleteSensorLabels = async (deviceName) => {
    try {
      const labelsRef = doc(firebase.db, 'deviceTypes', deviceName)
      const labelsSnapShot = await getDoc(labelsRef)

      if (labelsSnapShot.exists()) {
        return labelsSnapShot.data()
      }
    } catch (error) {
      console.log('No such document!')
    }
  }

  const fetchDataForCSV = async (sessionId, sensorId, userDevices) => {
    try {
      const leftSensorData = []
      const rightSensorData = []
      const leftOffsetData = []
      const rightOffsetData = []

      const q = query(
        collection(firebase.db, 'data_by_sensor'),
        where('sessionId', '==', sessionId),
        where('sensorId', '==', sensorId),
        orderBy('binId')
      )
      const querySnapshot = await getDocs(q)

      let firstLeftTimestamp = null
      let firstRightTimestamp = null

      querySnapshot.forEach((doc) => {
        const values = doc.data().values
        if (sensorId === 'Time') {
          // Process each document
          const deviceType = userDevices[doc.data().deviceId]
          values.forEach(value => {
            const dateInMs = value.toDate().getTime()

            if (deviceType === 'Left') {
              leftSensorData.push(formatToJsDate(value))
              if (firstLeftTimestamp === null) {
                firstLeftTimestamp = dateInMs
              }
              leftOffsetData.push((dateInMs - firstLeftTimestamp) / 1000)
            } else {
              rightSensorData.push(formatToJsDate(value))
              if (firstRightTimestamp === null) {
                firstRightTimestamp = dateInMs
              }
              rightOffsetData.push((dateInMs - firstRightTimestamp) / 1000)
            }
          })
        } else {
          if (userDevices[doc.data().deviceId] === 'Left') {
            leftSensorData.push(...values)
          } else {
            rightSensorData.push(...values)
          }
        }
      })

      if (sensorId === 'Time') {
        return {
          leftSensorData: leftSensorData.length ? [{ data: leftSensorData, name: 'Time' }, { data: leftOffsetData, name: 'Offset' }] : [],
          rightSensorData: rightSensorData.length ? [{ data: rightSensorData, name: 'Time' }, { data: rightOffsetData, name: 'Offset' }] : []
        }
      }

      return {
        leftSensorData: leftSensorData.length ? [{ data: leftSensorData, name: sensorId }] : [],
        rightSensorData: rightSensorData.length ? [{ data: rightSensorData, name: sensorId }] : []
      }
    } catch (error) {
      console.log('Firebase error', error)
    }
  }

  return { fetchTimeData, fetchDataForChart, formatTime, fetchSensorLabels, fetchCompleteSensorLabels, fetchDataForCSV, fetchremovedTimePoints, fetchTimeDataOffsets }
}
export default getSensorData
